import { User as CoreUser } from '@policyfly/protobuf'
import {
  ListActivityRequest,
  ListActivityRequest_Type,
  ListActivityResponse,
  MarkAllCommentsReadRequest,
  MarkAllCommentsReadResponse,
  PatrickServiceClient,
} from '@policyfly/protobuf/patrick'

import { useAuthenticationStore } from '@/stores/authentication'
import { ACTIVITY_PAGE_SIZE } from '@/stores/feed'

import { api } from '@/api'
import { devtools } from '@/plugins/devtools/api'
import { activityFeedItemToFeedEventLog } from '@/utils/protobuf'

import type { GrpcLogLevel } from '@/plugins/devtools/api'
import type { ApiVariableEndpoints, CreateEndpointParams } from '@/stores/api'
import type { ActivityFeedItem } from '@policyfly/protobuf/pfactivity'
import type { FeedEventLog } from 'types/events'

export interface ActivityApiEndpoints {
  /**
   * Lists all activity items for a specified id.
   */
  list: (request: ListActivityRequest, logLevel?: GrpcLogLevel) => Promise<{
    items?: ActivityFeedItem[]
    json: FeedEventLog[]
    count: number
    /**
     * Only returned by django, pfactivity cannot determine an unread count.
     */
    unreadCommentCount?: number
  }>
  /**
   * Mark all comments for an application or policy as read.
   */
  markAllCommentsRead: (request: MarkAllCommentsReadRequest) => Promise<{
    items?: ActivityFeedItem[]
    json: FeedEventLog[]
  }>
}

/**
 * Creates endpoints related to the Activity feed.
 */
export const createActivityEndpoints = (params: CreateEndpointParams): ApiVariableEndpoints<ActivityApiEndpoints> => {
  const authenticationStore = useAuthenticationStore()
  const patrickServiceClient = new PatrickServiceClient(params.transport)

  // django has no activity service, the events are read from the application or policy they belong to.
  // They already include comments, and are never paginated.
  const listDjango: ActivityApiEndpoints['list'] = async (request) => {
    if (request.type === ListActivityRequest_Type.TYPE_POLICY) {
      const { data } = await api.policies.read({ path: { pk: +request.id }, query: {} })
      const json = (data.Policy.events || []) as FeedEventLog[]
      return {
        json,
        count: json.length,
        unreadCommentCount: data.Policy.unread_comment_count || 0,
      }
    }

    const { data } = await api.applications.read({ path: { pk: +request.id }, query: {} })
    const json = (data.Application.events || []) as FeedEventLog[]
    return {
      json,
      count: json.length,
      unreadCommentCount: data.Application.unread_comment_count || 0,
    }
  }

  const listGrpc: ActivityApiEndpoints['list'] = async (request, logLevel) => {
    if (!request.paginationOptions) {
      request.paginationOptions = {
        page: 0,
        limit: ACTIVITY_PAGE_SIZE,
      }
    }
    const { response } = await patrickServiceClient.listActivity(request)

    devtools.logGrpc({
      description: 'List Events',
      messages: [
        { type: ListActivityRequest, key: 'request', message: request },
        { type: ListActivityResponse, key: 'response', message: response },
      ],
      logLevel,
    })

    return {
      items: response.items,
      json: response.items
        .map(activityFeedItemToFeedEventLog)
        .filter((item): item is FeedEventLog => item !== null),
      count: response.count,
    }
  }

  const markAllCommentsReadDjango: ActivityApiEndpoints['markAllCommentsRead'] = async (request) => {
    let data
    switch (request.reference.oneofKind) {
      case 'policyId':
        ({ data } = await api.comments.mark_policy_read({ path: { policy_pk: +request.reference.policyId } }))
        break
      case 'applicationId':
        ({ data } = await api.comments.mark_application_read({ path: { app_pk: +request.reference.applicationId } }))
        break
    }
    if (!data) throw new Error('Invalid mark comments request for django')

    return {
      json: data.Events || [],
    }
  }
  const markAllCommentsReadGrpc: ActivityApiEndpoints['markAllCommentsRead'] = async (payload) => {
    const request = MarkAllCommentsReadRequest.create({ user: CoreUser.clone(authenticationStore.coreUser), ...payload })
    let followUpReadRequest
    switch (request.reference.oneofKind) {
      case 'policyId':
        followUpReadRequest = ListActivityRequest.create({ id: request.reference.policyId, type: ListActivityRequest_Type.TYPE_POLICY })
        break
      case 'applicationId':
        followUpReadRequest = ListActivityRequest.create({ id: request.reference.applicationId, type: ListActivityRequest_Type.TYPE_APPLICATION })
        break
    }
    if (!followUpReadRequest) throw new Error('Invalid mark comments request for grpc')

    const { response } = await patrickServiceClient.markAllCommentsRead(request)

    devtools.logGrpc({
      description: 'Mark All Comments Read',
      messages: [
        { type: MarkAllCommentsReadRequest, key: 'request', message: request },
        { type: MarkAllCommentsReadResponse, key: 'response', message: response },
      ],
    })

    return listGrpc(followUpReadRequest)
  }

  return {
    django: {
      list: listDjango,
      markAllCommentsRead: markAllCommentsReadDjango,
    },
    grpc: {
      list: listGrpc,
      markAllCommentsRead: markAllCommentsReadGrpc,
    },
  }
}
